import React, { useEffect, useState } from "react";
import { Coffee, Flame, ExternalLink, ShieldCheck } from "lucide-react";
import { api } from "@/api";
import { CopyButton } from "@/components/CopyButton";
import { RoastBuy } from "@/components/roast/RoastBuy";

const EXP = "https://explorer.cronos.org";
const n = (v) => Number(v).toLocaleString();

function Stat({ label, value, sub, tid, accent = "" }) {
  return (
    <div className="card-tile" data-testid={tid}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`font-mono text-2xl sm:text-3xl font-black mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function BurnTable({ burns }) {
  if (!burns.length) return <p className="text-sm text-slate-500 mt-3" data-testid="roast-no-burns">No quarterly burns yet. The first one will appear here as soon as it's confirmed on-chain.</p>;
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-sm" data-testid="roast-burn-table">
        <thead><tr className="text-left text-xs text-slate-500"><th className="py-2">Quarter</th><th>Sales</th><th>ROAST burned</th><th>Date</th><th>Proof</th></tr></thead>
        <tbody>
          {burns.map((b) => (
            <tr key={b.tx_hash + b.period} className="border-t border-white/5" data-testid={`roast-burn-${b.period}`}>
              <td className="py-3 font-bold">{b.period}</td><td className="font-mono">{n(b.sales)}</td>
              <td className="font-mono text-orange-300">{n(b.amount)}</td><td className="text-slate-400">{new Date(b.time).toLocaleDateString()}</td>
              <td><a href={`${EXP}/tx/${b.tx_hash}`} target="_blank" rel="noreferrer" className="text-cyan-400 underline text-xs">View tx ↗</a></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function RoastPage() {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState("");
  const load = () => api.get("/roast").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, []);
  if (!d) return <div className="pt-32 text-center text-slate-400">Loading…</div>;
  const pct = (d.burned / (d.max_supply - d.floor)) * 100;
  const add = () => (window.ethereum
    ? window.ethereum.request({ method: "wallet_watchAsset", params: { type: "ERC20", options: { address: d.address, symbol: "ROAST", decimals: 18 } } })
      .then(() => setMsg("ROAST added to your wallet")).catch((e) => setMsg(e.message))
    : setMsg("No wallet found"));
  return (
    <div className="pt-24 pb-20" data-testid="roast-page">
      <div className="max-w-5xl mx-auto px-4">
        <div className="chip inline-flex text-amber-300 border-amber-400/40"><Coffee size={12} /> Burned by coffee sales</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-3">Northwoods Roast <span className="text-slate-500 text-2xl">ROAST</span></h1>
        <p className="text-base md:text-lg text-slate-300 mt-3 max-w-3xl">Every sale at <a href={d.store_url} target="_blank" rel="noreferrer" className="text-orange-300 underline">northwoodroast.com</a> burns 100 ROAST. Burns happen each quarter and are recorded on-chain, until only 20 million remain.</p>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <code className="font-mono text-xs sm:text-sm break-all" data-testid="roast-address">{d.address}</code>
          <CopyButton value={d.address} tid="roast-copy-address" label="ROAST address" />
          <a href={`${EXP}/address/${d.address}#code`} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 underline flex items-center gap-1">Explorer <ExternalLink size={11} /></a>
          <button onClick={add} className="btn-tactile btn-ghost text-xs" data-testid="roast-add-wallet">Add to wallet</button>
          {msg && <span className="text-xs text-slate-400">{msg}</span>}
        </div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Live supply" value={n(d.supply)} sub={`of ${n(d.max_supply)} max`} tid="roast-stat-supply" />
          <Stat label="Total burned" value={n(d.burned)} sub="permanently destroyed" tid="roast-stat-burned" accent="text-orange-300" />
          <Stat label="Sales counted" value={n(d.sales_burned)} sub="100 ROAST each" tid="roast-stat-sales" />
          <Stat label="Sales until the floor" value={n(d.sales_left)} sub={`${n(d.burnable_left)} ROAST left to burn`} tid="roast-stat-left" />
        </div>

        <div className="card-tile mt-6" data-testid="roast-progress">
          <div className="flex justify-between text-sm"><span className="flex items-center gap-1"><Flame size={14} className="text-orange-400" /> Burn progress toward the 20M floor</span><span className="font-mono" data-testid="roast-progress-pct">{pct.toFixed(3)}%</span></div>
          <div className="mt-3 h-3 rounded-full bg-[#12131C] overflow-hidden"><div className="h-full bg-gradient-to-r from-orange-500 to-amber-400 transition-[width] duration-700" style={{ width: `${Math.max(pct, 0.5)}%` }} /></div>
          <p className="mt-3 text-xs text-slate-500 flex items-center gap-1"><ShieldCheck size={12} className="text-emerald-400" /> The coin's own code blocks any burn below 20,000,000. It has no owner, and no new coins can ever be created.</p>
        </div>

        {d.sale && <RoastBuy sale={d.sale} onDone={load} />}

        <div className="card-tile mt-6" data-testid="roast-burn-history">
          <h2 className="font-display text-2xl font-bold">Quarterly burns</h2>
          <BurnTable burns={d.burns} />
        </div>
      </div>
    </div>
  );
}
